import { useEffect, useMemo, useRef, useState } from "react";
import { MapPin, Search, X } from "lucide-react";
import { buildSuggestions, type MapItem } from "@/lib/map-search";
import { loadPlaces } from "@/lib/google-maps";
import { now, recordPlacesLookup } from "@/lib/perf";


interface PlaceHit {
  id: string;
  label: string;
  placeId: string;
}

/**
 * Map search bar — matches city, area, locality, pincode and landmark from the
 * loaded listings, plus Google Places predictions (restricted to India) so the
 * map can jump to any place that has no listings yet.
 */
export function MapSearchBar({
  value,
  onChange,
  items,
  onPickPlace,
  placeholder = "Search city, area, locality, pincode or landmark",
}: {
  value: string;
  onChange: (v: string) => void;
  items: MapItem[];
  /** Called with the resolved geometry of a selected Google place. */
  onPickPlace?: (place: { label: string; lat: number; lng: number }) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [places, setPlaces] = useState<PlaceHit[]>([]);
  const boxRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const suggestions = useMemo(() => buildSuggestions(items, value), [items, value]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  /** Debounced Google Places predictions — only when a consumer can use them. */
  useEffect(() => {
    if (!onPickPlace) return;
    const term = value.trim();
    if (term.length < 3) {
      setPlaces([]);
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const startedAt = now();
      try {
        const lib = await loadPlaces();
        sessionRef.current ??= new lib.AutocompleteSessionToken();
        const { suggestions: hits } = await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            input: term,
            includedRegionCodes: ["in"],
            sessionToken: sessionRef.current,
          },
        );
        recordPlacesLookup(now() - startedAt);
        if (cancelled) return;
        setPlaces(

          hits
            .map((h) => h.placePrediction)
            .filter((p): p is google.maps.places.PlacePrediction => Boolean(p))
            .slice(0, 5)
            .map((p) => ({
              id: p.placeId,
              placeId: p.placeId,
              label: p.text.toString(),
            })),
        );
      } catch {
        // Places unavailable (key/API/network) — listing suggestions still work.
        if (!cancelled) setPlaces([]);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [value, onPickPlace]);

  const choosePlace = async (hit: PlaceHit) => {
    setOpen(false);
    onChange("");
    const startedAt = now();
    try {
      const lib = await loadPlaces();
      const place = new lib.Place({ id: hit.placeId });
      await place.fetchFields({ fields: ["location", "displayName", "viewport"] });
      recordPlacesLookup(now() - startedAt);
      const loc = place.location;
      if (loc) onPickPlace?.({ label: hit.label, lat: loc.lat(), lng: loc.lng() });

    } catch {
      /* geometry unavailable — leave the map where it is */
    } finally {
      sessionRef.current = null;
    }
  };

  const hasMenu = open && (suggestions.length > 0 || places.length > 0);

  return (
    <div ref={boxRef} className="relative w-full">
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        aria-label="Search listings by city, area, locality, pincode or landmark"
        className="w-full rounded-full border border-border bg-background py-2.5 pl-10 pr-9 text-sm shadow-float outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/30"
      />
      {value && (
        <button
          type="button"
          aria-label="Clear search"
          onClick={() => {
            onChange("");
            setOpen(false);
          }}
          className="absolute right-2.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-secondary text-brown-dark hover:bg-brand-soft"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {hasMenu && (
        <div className="absolute inset-x-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-border bg-background shadow-float">
          {suggestions.map((s) => (
            <button
              key={`${s.kind}-${s.label}`}
              type="button"
              onClick={() => {
                onChange(s.label);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm text-brown-dark transition hover:bg-brand-soft"
            >
              <span className="truncate">{s.label}</span>
              <span className="shrink-0 rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {s.kind}
              </span>
            </button>
          ))}
          {places.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => void choosePlace(p)}
              className="flex w-full items-center gap-2 border-t border-border px-4 py-2.5 text-left text-sm text-brown-dark transition hover:bg-brand-soft"
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="truncate">{p.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
